import { useCallback, useEffect, useMemo, useState } from "react";

export const useReferentiallyStableRawStorage = (storage: Storage, key: string) => {
	const [value, setValue] = useState<string | null>(() => storage.getItem(key));

	useEffect(() => {
		const handleStorageEvent = (e: StorageEvent) => {
			if (e.storageArea !== storage || e.key !== key) {
				return;
			}

			setValue(storage.getItem(key));
		};

		window.addEventListener("storage", handleStorageEvent);

		return () => window.removeEventListener("storage", handleStorageEvent);
	}, [storage, key]);

	const set = useCallback(
		(newValue: string) => {
			storage.setItem(key, newValue);
			setValue(newValue);

			window.dispatchEvent(new StorageEvent("storage", { key, storageArea: storage }));
		},
		[storage, key],
	);

	const remove = useCallback(() => {
		storage.removeItem(key);
		setValue(null);

		window.dispatchEvent(new StorageEvent("storage", { key, storageArea: storage }));
	}, [storage, key]);

	const fetch = useCallback(() => {
		setValue(storage.getItem(key));
	}, [storage, key]);

	return useMemo(() => ({ value, set, remove, fetch }), [value, set, remove, fetch]);
};
